import React from 'react';
import PageHeader from '../components/PageHeader';
import { motion } from 'framer-motion';

const Data = () => {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="prose-custom">
      <PageHeader 
        badge="Stage 2"
        title="Data Collection & Management" 
        subtitle="How you store, structure, and describe your data determines if it will ever be usable again—even by you."
      />

      <p>
        Data management is often viewed as a bureaucratic hurdle (like writing a Data Management Plan for a grant). 
        In reality, it is the bedrock of your daily lab function. Poor data management means lost files, unreadable 
        spreadsheets, and uninterpretable results when a grad student graduates.
      </p>

      <h2>The FAIR Principles</h2>
      <p>
        The FAIR guiding principles were published in 2016 to provide guidelines to improve the reusability of academic data. 
        They apply equally to human-readable and machine-readable data.
      </p>

      <div className="grid sm:grid-cols-2 gap-4 my-8">
        <div className="bg-white p-5 rounded border border-slate-200">
          <h4 className="text-lg font-bold text-slate-800 mb-2">Findable</h4>
          <p className="text-sm m-0">Data should have rich metadata and a unique, persistent identifier (like a DOI). It should be registered in a searchable resource.</p>
        </div>
        <div className="bg-white p-5 rounded border border-slate-200">
          <h4 className="text-lg font-bold text-slate-800 mb-2">Accessible</h4>
          <p className="text-sm m-0">Data can be retrieved by its identifier using a standardized, open protocol. (Note: Accessible does not always mean "open to everyone" if data is sensitive).</p>
        </div>
        <div className="bg-white p-5 rounded border border-slate-200">
          <h4 className="text-lg font-bold text-slate-800 mb-2">Interoperable</h4>
          <p className="text-sm m-0">Data uses formal, accessible, shared, and broadly applicable vocabularies. Use non-proprietary formats (e.g., .csv instead of .xlsx).</p>
        </div>
        <div className="bg-white p-5 rounded border border-slate-200">
          <h4 className="text-lg font-bold text-slate-800 mb-2">Reusable</h4>
          <p className="text-sm m-0">Data is released with a clear data usage license, detailed provenance, and meets domain-relevant community standards.</p>
        </div>
      </div>

      <h2>Electronic Lab Notebooks (ELNs) & Provenance</h2>
      <p>
        Paper notebooks are susceptible to loss, illegibility, and lack searchability. Transitioning to an Electronic 
        Lab Notebook (ELN) ensures that the provenance of your data—the exact chronological history of how data was 
        acquired and modified—is preserved.
      </p>
      
      <h3>Data Management Plans (DMPs)</h3>
      <p>
        A DMP should be a living document in your lab, not a static PDF sent to a funder. It should define:
      </p>
      <ul>
        <li>File naming conventions (e.g., `YYYYMMDD_ExperimentName_Initials`).</li>
        <li>Folder hierarchy structures.</li>
        <li>Backup strategies (the 3-2-1 rule: 3 copies, 2 different media, 1 offsite).</li>
        <li>Metadata standards (what variables are recorded for every sample).</li>
      </ul>
    </motion.div>
  );
};

export default Data;